/**
 * =============================================================================
 * UserNotificationBell.tsx - Campanella notifiche utente
 * =============================================================================
 * 
 * Componente campanella per notifiche sistema (promemoria corsi, scadenze, ecc.)
 * Distinto da ForumNotificationBell che gestisce solo le notifiche forum.
 * 
 * @author CCFN Development Team
 * =============================================================================
 */
import { Bell, Calendar, CreditCard, Award, AlertTriangle, Check, CheckCheck, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { useUserNotifications, UserNotification } from "@/hooks/useUserNotifications";
import { useNavigate } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import { it } from "date-fns/locale";
import { cn } from "@/lib/utils";

/**
 * Restituisce l'icona appropriata in base alla categoria della notifica
 */
const getNotificationIcon = (category: string) => {
  switch (category) {
    case "course":
      return <Calendar className="h-4 w-4 text-primary" />;
    case "payment":
      return <CreditCard className="h-4 w-4 text-amber-500" />;
    case "certification":
      return <Award className="h-4 w-4 text-emerald-500" />;
    case "overdue":
      return <AlertTriangle className="h-4 w-4 text-destructive" />;
    default:
      return <Bell className="h-4 w-4 text-muted-foreground" />;
  }
};

/**
 * Restituisce il colore del badge in base al tipo di notifica
 */
const getNotificationVariant = (type: string): "default" | "secondary" | "destructive" | "outline" => {
  if (type.includes("urgent") || type.includes("overdue")) {
    return "destructive";
  }
  if (type.includes("second")) {
    return "secondary";
  }
  return "outline";
};

export const UserNotificationBell = () => {
  const { 
    notifications, 
    unreadCount, 
    markAsRead, 
    markAllAsRead,
    isMarkingAllAsRead,
    isLoading
  } = useUserNotifications();
  const navigate = useNavigate();

  const handleNotificationClick = (notification: UserNotification) => {
    if (!notification.is_read) {
      markAsRead(notification.id);
    }
    if (notification.action_url) {
      navigate(notification.action_url);
    }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <Badge 
              variant="destructive" 
              className="absolute -top-1 -right-1 h-5 w-5 p-0 flex items-center justify-center text-xs"
            >
              {unreadCount > 9 ? "9+" : unreadCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-96 p-0" align="end">
        {/* Header */}
        <div className="flex items-center justify-between p-3 border-b">
          <h4 className="font-semibold text-sm">Notifiche</h4>
          {unreadCount > 0 && (
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-7 text-xs"
              onClick={() => markAllAsRead()}
              disabled={isMarkingAllAsRead}
            >
              <CheckCheck className="h-3 w-3 mr-1" />
              Segna tutte lette
            </Button>
          )}
        </div>
        
        {/* Notifications List */}
        <ScrollArea className="h-96">
          {isLoading ? (
            <div className="p-6 text-center text-muted-foreground text-sm">
              Caricamento...
            </div>
          ) : notifications.length === 0 ? (
            <div className="p-6 text-center text-muted-foreground text-sm">
              <Bell className="h-8 w-8 mx-auto mb-2 opacity-30" />
              Nessuna notifica
            </div>
          ) : (
            <div className="divide-y">
              {notifications.map((notification) => (
                <button
                  key={notification.id}
                  className={cn(
                    "w-full p-3 text-left hover:bg-muted/50 transition-colors flex gap-3",
                    !notification.is_read && "bg-primary/5"
                  )}
                  onClick={() => handleNotificationClick(notification)}
                >
                  {/* Icon */}
                  <div className="flex-shrink-0 mt-0.5">
                    {getNotificationIcon(notification.category)}
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <p className={cn(
                        "text-sm line-clamp-1 flex-1",
                        !notification.is_read && "font-medium"
                      )}>
                        {notification.title}
                      </p>
                      <Badge 
                        variant={getNotificationVariant(notification.notification_type)}
                        className="text-[10px] px-1.5 py-0"
                      >
                        {notification.notification_type.includes("urgent") ? "Urgente" : 
                         notification.notification_type.includes("overdue") ? "Scaduto" : 
                         notification.notification_type.includes("second") ? "2° avviso" : 
                         "Avviso"}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground line-clamp-2">
                      {notification.message}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      <p className="text-xs text-muted-foreground">
                        {formatDistanceToNow(new Date(notification.created_at), {
                          addSuffix: true,
                          locale: it,
                        })}
                      </p>
                      {notification.action_url && notification.action_label && (
                        <span className="text-xs text-primary flex items-center gap-0.5">
                          <ExternalLink className="h-3 w-3" />
                          {notification.action_label}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {/* Unread indicator */}
                  {!notification.is_read && (
                    <div className="flex-shrink-0">
                      <div className="h-2 w-2 rounded-full bg-primary" />
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}
        </ScrollArea>

        {/* Footer */}
        <div className="p-2 border-t">
          <Button 
            variant="ghost" 
            className="w-full text-sm"
            onClick={() => navigate("/dashboard")}
          >
            Vai alla Dashboard
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};
